// src/prototype/components/sep/SEPSectionPortfolioImpact.tsx
// Component 7: financial_summary

import React from 'react';
import { safeNumber, type MappedFinancialSummary } from '../../utils/mapStrategicExecutionPlan';

export interface SEPSectionPortfolioImpactProps {
  financialSummary: MappedFinancialSummary | null;
}

const COLORS = ['#18365E', '#FF6E4C', '#2563EB'];

const fmt = (n: number | null | undefined) => `$${safeNumber(n).toFixed(1)}M`;

export const SEPSectionPortfolioImpact: React.FC<SEPSectionPortfolioImpactProps> = ({
  financialSummary,
}) => {
  if (!financialSummary) {
    return (
      <div className="bg-white border border-[#e5e7eb] rounded-xl p-6">
        <p className="text-sm text-[#717182]">Portfolio financial impact data is not available.</p>
      </div>
    );
  }

  const {
    portfolioRoiLow,
    portfolioRoiHigh,
    initiativesSelectedCount,
    roiComparison,
    scenarioTotals,
    waterfallTable,
  } = financialSummary;

  const maxHigh = Math.max(...roiComparison.map(row => row.roiHigh), 0.1);

  return (
    <div className="bg-white border border-[#e5e7eb] rounded-xl p-6">
      <div className="flex items-start gap-3 mb-6">
        <div className="w-[3px] self-stretch rounded-full bg-[#FF6E4C] shrink-0 min-h-[40px]" />
        <div>
          <p className="text-[10px] font-bold text-[#FF6E4C] uppercase tracking-widest mb-0.5">Section 3</p>
          <h2 className="text-lg font-bold text-[#18365E] leading-tight">Portfolio Financial Impact</h2>
          <p className="text-[11px] text-[#717182] mt-0.5">Combined ROI potential by initiative and scenario-based projections</p>
        </div>
      </div>

      <p className="text-sm text-[#18365E]/80 leading-relaxed mb-6">
        The selected portfolio represents a combined annual value opportunity of{' '}
        <strong>{fmt(portfolioRoiLow)}–{fmt(portfolioRoiHigh)}</strong> across all initiatives.
        Each initiative carries independently validated ROI assumptions derived from industry benchmarks and{' '}
        {initiativesSelectedCount > 1 ? `your ${initiativesSelectedCount} selected programs` : 'your selected program'}.
      </p>

      <div className="mb-6">
        <p className="text-[10px] font-semibold text-[#717182] uppercase tracking-wide mb-3">Initiative ROI Comparison</p>
        <div className="bg-[#F5F7FA] rounded-xl p-4 border border-[#e5e7eb] space-y-4">
          {roiComparison.map((row, i) => {
            const color = COLORS[i % COLORS.length];
            const roiLow = safeNumber(row.roiLow);
            const roiHigh = safeNumber(row.roiHigh);
            const barWidth = row.barWidthPercent || ((roiHigh / maxHigh) * 100);
            return (
              <div key={row.id}>
                <div className="flex items-center justify-between mb-1.5">
                  <div className="flex items-center gap-2">
                    <div className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: color }} />
                    <p className="text-xs font-semibold text-[#18365E] leading-tight">{row.name}</p>
                  </div>
                  <p className="text-xs font-bold" style={{ color }}>
                    ${roiLow.toFixed(1)}–${roiHigh.toFixed(1)}M/yr
                  </p>
                </div>
                <div className="relative h-4 bg-white rounded-full border border-[#e5e7eb] overflow-hidden">
                  <div
                    className="absolute top-0 bottom-0 rounded-full"
                    style={{
                      left: `${Math.max(0, (roiLow / maxHigh) * 85)}%`,
                      width: `${Math.max(2, barWidth * 0.85)}%`,
                      background: color,
                      opacity: 0.35,
                    }}
                  />
                  <div
                    className="absolute top-0 bottom-0 rounded-full"
                    style={{
                      left: `${Math.max(0, (roiLow / maxHigh) * 85)}%`,
                      width: `${Math.max(1, (roiLow / maxHigh) * 5)}%`,
                      background: color,
                    }}
                  />
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div>
        <p className="text-[10px] font-semibold text-[#717182] uppercase tracking-wide mb-3">Scenario Analysis — Combined Portfolio</p>
        <div className="border border-[#e5e7eb] rounded-xl overflow-hidden">
          <table className="w-full text-xs">
            <thead>
              <tr className="bg-[#F5F7FA] border-b border-[#e5e7eb]">
                <th className="text-left px-4 py-2.5 font-semibold text-[#717182] w-[160px]">Initiative</th>
                {['Conservative', 'Base Case', 'Aggressive'].map(s => (
                  <th key={s} className="text-center px-3 py-2.5 font-semibold text-[#717182]">{s}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {waterfallTable.map((row, i) => (
                <tr key={row.id} className={i < waterfallTable.length - 1 ? 'border-b border-[#e5e7eb]' : ''}>
                  <td className="px-4 py-3 font-medium text-[#18365E]">{row.name}</td>
                  <td className="px-3 py-3 text-center">
                    <p className="font-bold text-[#18365E]">{fmt(row.conservative)}</p>
                    {(row.paybackMonthsConservative ?? row.paybackMonths) != null && (
                      <p className="text-[10px] text-[#717182]">
                        {row.paybackMonthsConservative ?? row.paybackMonths}mo payback
                      </p>
                    )}
                  </td>
                  <td className="px-3 py-3 text-center">
                    <p className="font-bold text-[#18365E]">{fmt(row.base)}</p>
                    {(row.paybackMonthsBase ?? row.paybackMonths) != null && (
                      <p className="text-[10px] text-[#717182]">
                        {row.paybackMonthsBase ?? row.paybackMonths}mo payback
                      </p>
                    )}
                  </td>
                  <td className="px-3 py-3 text-center">
                    <p className="font-bold text-[#18365E]">{fmt(row.aggressive)}</p>
                    {(row.paybackMonthsAggressive ?? row.paybackMonths) != null && (
                      <p className="text-[10px] text-[#717182]">
                        {row.paybackMonthsAggressive ?? row.paybackMonths}mo payback
                      </p>
                    )}
                  </td>
                </tr>
              ))}
              <tr className="bg-[#18365E]/[0.04] border-t border-[#18365E]/10">
                <td className="px-4 py-3 font-bold text-[#18365E] text-[11px] uppercase tracking-wide">Total Portfolio</td>
                <td className="px-3 py-3 text-center">
                  <p className="font-bold text-[#FF6E4C] text-sm">{fmt(scenarioTotals.conservative)}</p>
                </td>
                <td className="px-3 py-3 text-center">
                  <p className="font-bold text-[#FF6E4C] text-sm">{fmt(scenarioTotals.base)}</p>
                </td>
                <td className="px-3 py-3 text-center">
                  <p className="font-bold text-[#FF6E4C] text-sm">{fmt(scenarioTotals.aggressive)}</p>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default SEPSectionPortfolioImpact;
